/**
 * Which mounted screens hold work that is not saved yet.
 *
 * Pages that warn before the browser closes (a beforeunload handler while a
 * form is dirty) also call useUnsavedWork with the same flag, so code outside
 * the page can ask "would a reload lose something?" without dispatching a fake
 * beforeunload (other beforeunload handlers have side effects, such as the
 * heartbeat clear in AuthTokenSetter). The other signal for the same question
 * is `data-dirty="true"` on a page's root element (see hasUnsavedWork).
 *
 * Used by the quiet update (utils/updateMoment.ts) and the facility switcher.
 */
import { useEffect } from 'react';

const holders = new Set<symbol>();

/** True while any mounted screen says it has unsaved work, or marks itself data-dirty. */
export function hasUnsavedWork(doc: Document | null = typeof document !== 'undefined' ? document : null): boolean {
  if (holders.size > 0) return true;
  return !!doc?.querySelector('[data-dirty="true"]');
}

/** Marks this screen as holding unsaved work while `dirty` is true. */
export function useUnsavedWork(dirty: boolean): void {
  useEffect(() => {
    if (!dirty) return undefined;
    const token = Symbol('unsaved');
    holders.add(token);
    return () => {
      holders.delete(token);
    };
  }, [dirty]);
}

/** Tests only. */
export function resetUnsavedWorkForTests(): void {
  holders.clear();
}
